import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

// Lending partners from the official Catalyst Global Partners brochure.
// Logos are referenced from publicly hosted sources (Wikipedia) and stored locally for stability.
const PARTNERS = [
  {
    name: "State Bank of India",
    short: "SBI",
    logo: "/assets/partners/sbi.svg",
    sub: "Public Sector Bank",
  },
  {
    name: "HDFC Bank",
    short: "HDFC Bank",
    logo: "/assets/partners/hdfc.svg",
    sub: "Private Sector Bank",
  },
  {
    name: "HSBC",
    short: "HSBC",
    logo: "/assets/partners/hsbc.svg",
    sub: "International Bank",
  },
  {
    name: "Axis Bank",
    short: "Axis Bank",
    logo: "/assets/partners/axis.svg",
    sub: "Private Sector Bank",
  },
  {
    name: "Yes Bank",
    short: "Yes Bank",
    logo: "/assets/partners/yes.svg",
    sub: "Private Sector Bank",
  },
  {
    name: "Kotak Mahindra Bank",
    short: "Kotak",
    logo: "/assets/partners/kotak.svg",
    sub: "Private Sector Bank",
  },
  {
    name: "Aditya Birla Capital",
    short: "Aditya Birla",
    logo: "/assets/partners/aditya_birla.png",
    sub: "NBFC · Group Co.",
  },
  {
    // No public logo available for Capsave Finance — render an elegant text wordmark.
    name: "Capsave Finance",
    short: "Capsave Finance",
    logo: null,
    sub: "Specialised NBFC",
  },
];

const NETWORK = [
  "PSU Banks",
  "Private Sector Banks",
  "NBFCs",
  "Private Equity Firms",
  "Venture Capital Partners",
  "HNIs & Family Offices",
  "Merchant Bankers",
  "Legal & Regulatory Advisors",
];

export default function LendingPartners() {
  return (
    <section
      id="lending-partners"
      data-testid="lending-partners-section"
      className="bg-[#FAFAF7] py-24 md:py-32 border-t border-[#0A1F44]/8"
    >
      <div className="cgp-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="cgp-overline">Our Lending Partners</span>
          <h2 className="font-serif font-light text-[#0A1F44] mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Backed by India's
            <br />
            <span className="italic text-[#0A1F44]/70">
              most trusted financial institutions.
            </span>
          </h2>
          <p className="mt-7 text-[#0B1A36]/70 text-lg leading-relaxed max-w-2xl">
            A deep, working relationship with leading public sector banks,
            private banks and NBFCs lets us structure the right capital — at
            the right cost — for every client we take on.
          </p>
        </motion.div>

        {/* Partner Grid */}
        <div
          data-testid="partners-grid"
          className="mt-16 grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#0A1F44]/10"
        >
          {PARTNERS.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              data-testid={`partner-${p.short.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-white border-r border-b border-[#0A1F44]/10 h-36 md:h-44 flex flex-col items-center justify-center px-6 py-6 transition-colors duration-300 hover:bg-[#0A1F44]/[0.02]"
              title={p.name}
            >
              {p.logo ? (
                <img
                  src={p.logo}
                  alt={p.name}
                  className="max-h-12 md:max-h-14 max-w-[80%] object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                />
              ) : (
                <span className="font-serif text-2xl md:text-[1.6rem] text-[#0A1F44] tracking-tight text-center leading-tight">
                  {p.short}
                </span>
              )}
              <span className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#0B1A36]/50 text-center">
                {p.sub}
              </span>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-[#0B1A36]/45 text-xs italic">
          All trademarks and logos are property of their respective owners and
          are referenced solely to indicate professional relationships.
        </p>

        {/* Network strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid="partner-network"
          className="mt-20 bg-white border border-[#0A1F44]/10 p-8 md:p-10"
        >
          <div className="flex items-start gap-4">
            <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#C8A24A]/12 border border-[#C8A24A]/30 text-[#C8A24A] shrink-0">
              <ShieldCheck size={18} strokeWidth={1.7} />
            </span>
            <div>
              <h3 className="font-serif text-2xl text-[#0A1F44]">
                Our wider funding network
              </h3>
              <p className="text-[#0B1A36]/65 mt-2 max-w-2xl leading-relaxed">
                Beyond the named banks, Catalyst plugs you into a curated
                ecosystem of capital providers:
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {NETWORK.map((n) => (
              <span
                key={n}
                data-testid={`network-tag-${n.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="inline-flex items-center px-4 py-2 bg-[#0A1F44]/5 hover:bg-[#C8A24A]/15 transition-colors text-[#0A1F44] text-sm border border-[#0A1F44]/10 hover:border-[#C8A24A]/40"
              >
                {n}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
